import React, { useState, useEffect } from 'react';
import {
  getSettings,
  updateSetting,
  getServiceCategories,
  createServiceCategory,
  updateServiceCategory,
  deleteServiceCategory,
  getDocumentTypes,
  createDocumentType,
  updateDocumentType,
  deleteDocumentType,
  getExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory,
  getCustomFields,
  createCustomField,
  updateCustomField,
  deleteCustomField,
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getEmirates,
  createEmirate,
  updateEmirate,
  deleteEmirate,
  getPaymentTerms,
  createPaymentTerm,
  updatePaymentTerm,
  deletePaymentTerm,
  getPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
  getOrderStatuses,
  createOrderStatus,
  updateOrderStatus,
  deleteOrderStatus,
  getAllPermissions,
  updateRolePermissions,
  resetPermissions
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Building2,
  FileText,
  Briefcase,
  Receipt,
  Users,
  Layers,
  Plus,
  Save,
  Edit,
  Trash2,
  MoreHorizontal,
  Shield,
  Bell,
  MapPin,
  CreditCard,
  Clock,
  GitBranch,
  Wallet,
  Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const entityTypes = ['Client', 'Worker', 'Service Order', 'Invoice', 'Document'];
const fieldTypes = ['Text', 'Long Text', 'Number', 'Date', 'Boolean', 'Select', 'Multi Select', 'Email', 'Phone', 'URL'];
const userRoles = ['Admin', 'Manager', 'Staff', 'Viewer'];

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [settings, setSettings] = useState({});
  const [serviceCategories, setServiceCategories] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [users, setUsers] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [formData, setFormData] = useState({});
  
  // Permissions state
  const [permissions, setPermissions] = useState({});
  const [permissionModules, setPermissionModules] = useState([]);
  const [permissionRoles, setPermissionRoles] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => { loadData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [
        settingsRes, catRes, docTypesRes, expCatRes, fieldsRes, usersRes,
        emiratesRes, payTermsRes, payMethodsRes, orderStatusRes, permissionsRes
      ] = await Promise.all([
        getSettings().catch(() => ({ data: [] })),
        getServiceCategories().catch(() => ({ data: [] })),
        getDocumentTypes().catch(() => ({ data: [] })),
        getExpenseCategories().catch(() => ({ data: [] })),
        getCustomFields().catch(() => ({ data: [] })),
        isAdmin ? getUsers().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        getEmirates().catch(() => ({ data: [] })),
        getPaymentTerms().catch(() => ({ data: [] })),
        getPaymentMethods().catch(() => ({ data: [] })),
        getOrderStatuses().catch(() => ({ data: [] })),
        isAdmin ? getAllPermissions().catch(() => ({ data: { permissions: {}, modules: [], roles: [] } })) : Promise.resolve({ data: { permissions: {}, modules: [], roles: [] } })
      ]);
      
      const settingsObj = {};
      (settingsRes.data || []).forEach(s => { settingsObj[s.setting_key] = s.setting_value; });
      setSettings(settingsObj);
      setServiceCategories(catRes.data || []);
      setDocumentTypes(docTypesRes.data || []);
      setExpenseCategories(expCatRes.data || []);
      setCustomFields(fieldsRes.data || []);
      setUsers(usersRes.data || []);
      setEmirates(emiratesRes.data || []);
      setPaymentTerms(payTermsRes.data || []);
      setPaymentMethods(payMethodsRes.data || []);
      setOrderStatuses(orderStatusRes.data || []);
      
      // Load permissions
      if (permissionsRes.data) {
        setPermissions(permissionsRes.data.permissions || {});
        setPermissionModules(permissionsRes.data.modules || []);
        setPermissionRoles(permissionsRes.data.roles || []);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSetting = async (key, value) => {
    try {
      await updateSetting(key, value);
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Setting saved');
    } catch (error) {
      toast.error('Failed to save setting');
    }
  };

  // Permission handling
  const handlePermissionChange = (role, module, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [module]: {
          ...prev[role]?.[module],
          [action]: value
        }
      }
    }));
  };

  const handleSavePermissions = async (role) => {
    setSavingPermissions(true);
    try {
      await updateRolePermissions(role, permissions[role]);
      toast.success(`Permissions saved for ${role}`);
    } catch (error) {
      toast.error('Failed to save permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleResetPermissions = async () => {
    if (!window.confirm('Are you sure you want to reset all permissions to defaults?')) return;
    try {
      const response = await resetPermissions();
      setPermissions(response.data.permissions || {});
      toast.success('Permissions reset to defaults');
    } catch (error) {
      toast.error('Failed to reset permissions');
    }
  };

  const openDialog = (type, item = null) => {
    setEditingItem(item ? { type, ...item } : { type });
    
    switch (type) {
      case 'serviceCategory':
        setFormData(item ? { name: item.name, code: item.code, icon: item.icon || '📋', description: item.description } : { name: '', code: '', icon: '📋', description: '' });
        break;
      case 'documentType':
        setFormData(item ? { name: item.name, code: item.code, requires_expiry: item.requires_expiry !== false, applicable_to: item.applicable_to || 'Both' } : { name: '', code: '', requires_expiry: true, applicable_to: 'Both' });
        break;
      case 'expenseCategory':
        setFormData(item ? { name: item.name, name_arabic: item.name_arabic, description: item.description } : { name: '', name_arabic: '', description: '' });
        break;
      case 'customField':
        setFormData(item ? { entity_type: item.entity_type, field_name: item.field_name, field_label: item.field_label, field_type: item.field_type, field_options: item.field_options?.join(', ') || '', is_required: item.is_required, help_text: item.help_text } : { entity_type: 'Client', field_name: '', field_label: '', field_type: 'Text', field_options: '', is_required: false, help_text: '' });
        break;
      case 'user':
        setFormData(item ? { email: item.email, full_name: item.full_name, role: item.role, is_active: item.is_active, password: '' } : { email: '', full_name: '', role: 'Staff', password: '' });
        break;
      case 'emirate':
        setFormData(item ? { code: item.code, name: item.name, name_arabic: item.name_arabic, display_order: item.display_order || 0 } : { code: '', name: '', name_arabic: '', display_order: 0 });
        break;
      case 'paymentTerm':
        setFormData(item ? { code: item.code, name: item.name, days: item.days || 0, display_order: item.display_order || 0 } : { code: '', name: '', days: 0, display_order: 0 });
        break;
      case 'paymentMethod':
        setFormData(item ? { code: item.code, name: item.name, name_arabic: item.name_arabic, icon: item.icon || '💵', display_order: item.display_order || 0 } : { code: '', name: '', name_arabic: '', icon: '💵', display_order: 0 });
        break;
      case 'orderStatus':
        setFormData(item ? { code: item.code, name: item.name, name_arabic: item.name_arabic, color: item.color || '#6B7280', display_order: item.display_order || 0, is_initial: item.is_initial || false, is_final: item.is_final || false } : { code: '', name: '', name_arabic: '', color: '#6B7280', display_order: 0, is_initial: false, is_final: false });
        break;
      default:
        setFormData({});
    }
    setDialogOpen(true);
  };

  const handleSaveItem = async () => {
    setSaving(true);
    try {
      const type = editingItem?.type;
      const isEdit = editingItem?.id;
      
      switch (type) {
        case 'serviceCategory':
          isEdit ? await updateServiceCategory(editingItem.id, formData) : await createServiceCategory(formData);
          break;
        case 'documentType':
          isEdit ? await updateDocumentType(editingItem.id, formData) : await createDocumentType(formData);
          break;
        case 'expenseCategory':
          isEdit ? await updateExpenseCategory(editingItem.id, formData) : await createExpenseCategory(formData);
          break;
        case 'customField':
          const fieldData = { ...formData, field_options: formData.field_options ? formData.field_options.split(',').map(s => s.trim()) : [] };
          isEdit ? await updateCustomField(editingItem.id, fieldData) : await createCustomField(fieldData);
          break;
        case 'user':
          isEdit ? await updateUser(editingItem.id, formData) : await createUser(formData);
          break;
        case 'emirate':
          isEdit ? await updateEmirate(editingItem.id, formData) : await createEmirate(formData);
          break;
        case 'paymentTerm':
          isEdit ? await updatePaymentTerm(editingItem.id, formData) : await createPaymentTerm(formData);
          break;
        case 'paymentMethod':
          isEdit ? await updatePaymentMethod(editingItem.id, formData) : await createPaymentMethod(formData);
          break;
        case 'orderStatus':
          isEdit ? await updateOrderStatus(editingItem.id, formData) : await createOrderStatus(formData);
          break;
        default:
          break;
      }
      
      toast.success(`${isEdit ? 'Updated' : 'Created'} successfully`);
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      switch (type) {
        case 'serviceCategory': await deleteServiceCategory(id); break;
        case 'documentType': await deleteDocumentType(id); break;
        case 'expenseCategory': await deleteExpenseCategory(id); break;
        case 'customField': await deleteCustomField(id); break;
        case 'user': await deleteUser(id); break;
        case 'emirate': await deleteEmirate(id); break;
        case 'paymentTerm': await deletePaymentTerm(id); break;
        case 'paymentMethod': await deletePaymentMethod(id); break;
        case 'orderStatus': await deleteOrderStatus(id); break;
        default: break;
      }
      toast.success('Deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getDialogTitle = () => {
    const typeMap = {
      'serviceCategory': 'Service Category',
      'documentType': 'Document Type',
      'expenseCategory': 'Expense Category',
      'customField': 'Custom Field',
      'user': 'User',
      'emirate': 'Emirate/Location',
      'paymentTerm': 'Payment Term',
      'paymentMethod': 'Payment Method',
      'orderStatus': 'Order Status'
    };
    return `${editingItem?.id ? 'Edit' : 'Add'} ${typeMap[editingItem?.type] || 'Item'}`;
  };

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="settings-loading">Loading settings...</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage system configuration and preferences</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <ScrollArea className="w-full">
          <TabsList className="inline-flex w-auto min-w-full h-auto flex-wrap gap-1 p-1">
            <TabsTrigger value="company" className="flex items-center gap-1"><Building2 className="w-4 h-4" /> Company</TabsTrigger>
            <TabsTrigger value="locations" className="flex items-center gap-1"><MapPin className="w-4 h-4" /> Locations</TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> Services</TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1"><FileText className="w-4 h-4" /> Documents</TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-1"><GitBranch className="w-4 h-4" /> Orders</TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-1"><Wallet className="w-4 h-4" /> Payments</TabsTrigger>
            <TabsTrigger value="expenses" className="flex items-center gap-1"><Receipt className="w-4 h-4" /> Expenses</TabsTrigger>
            <TabsTrigger value="fields" className="flex items-center gap-1"><Layers className="w-4 h-4" /> Fields</TabsTrigger>
            {isAdmin && <TabsTrigger value="users" className="flex items-center gap-1"><Users className="w-4 h-4" /> Users</TabsTrigger>}
            {isAdmin && <TabsTrigger value="permissions" className="flex items-center gap-1"><Shield className="w-4 h-4" /> Permissions</TabsTrigger>}
            <TabsTrigger value="notifications" className="flex items-center gap-1"><Bell className="w-4 h-4" /> Alerts</TabsTrigger>
          </TabsList>
        </ScrollArea>

        {/* Company Tab */}
        <TabsContent value="company" className="mt-6 space-y-6">
          <Card>
            <CardHeader><CardTitle>Business Information</CardTitle><CardDescription>Your company details for invoices and documents</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Company Name</Label><Input data-testid="company-name-input" value={settings.company_name || ''} onChange={(e) => setSettings(p => ({ ...p, company_name: e.target.value }))} onBlur={() => handleSaveSetting('company_name', settings.company_name)} /></div>
                <div><Label>Company Name (Arabic)</Label><Input value={settings.company_name_arabic || ''} onChange={(e) => setSettings(p => ({ ...p, company_name_arabic: e.target.value }))} onBlur={() => handleSaveSetting('company_name_arabic', settings.company_name_arabic)} dir="rtl" /></div>
                <div><Label>Trade License No.</Label><Input value={settings.license_no || ''} onChange={(e) => setSettings(p => ({ ...p, license_no: e.target.value }))} onBlur={() => handleSaveSetting('license_no', settings.license_no)} /></div>
                <div><Label>TRN (Tax Registration Number)</Label><Input value={settings.company_trn || ''} onChange={(e) => setSettings(p => ({ ...p, company_trn: e.target.value }))} onBlur={() => handleSaveSetting('company_trn', settings.company_trn)} /></div>
                <div className="col-span-full"><Label>Address</Label><Textarea value={settings.company_address || ''} onChange={(e) => setSettings(p => ({ ...p, company_address: e.target.value }))} onBlur={() => handleSaveSetting('company_address', settings.company_address)} rows={2} /></div>
                <div><Label>Phone</Label><Input value={settings.company_phone || ''} onChange={(e) => setSettings(p => ({ ...p, company_phone: e.target.value }))} onBlur={() => handleSaveSetting('company_phone', settings.company_phone)} /></div>
                <div><Label>Email</Label><Input value={settings.company_email || ''} onChange={(e) => setSettings(p => ({ ...p, company_email: e.target.value }))} onBlur={() => handleSaveSetting('company_email', settings.company_email)} /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Bank Details</CardTitle><CardDescription>Bank information for invoices</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Bank Name</Label><Input value={settings.bank_name || ''} onChange={(e) => setSettings(p => ({ ...p, bank_name: e.target.value }))} onBlur={() => handleSaveSetting('bank_name', settings.bank_name)} /></div>
                <div><Label>Account Number</Label><Input value={settings.bank_account || ''} onChange={(e) => setSettings(p => ({ ...p, bank_account: e.target.value }))} onBlur={() => handleSaveSetting('bank_account', settings.bank_account)} /></div>
                <div><Label>IBAN</Label><Input value={settings.bank_iban || ''} onChange={(e) => setSettings(p => ({ ...p, bank_iban: e.target.value }))} onBlur={() => handleSaveSetting('bank_iban', settings.bank_iban)} /></div>
                <div><Label>Swift Code</Label><Input value={settings.bank_swift || ''} onChange={(e) => setSettings(p => ({ ...p, bank_swift: e.target.value }))} onBlur={() => handleSaveSetting('bank_swift', settings.bank_swift)} /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Invoice Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Default VAT %</Label><Input type="number" value={settings.default_vat_percentage || '5'} onChange={(e) => setSettings(p => ({ ...p, default_vat_percentage: e.target.value }))} onBlur={() => handleSaveSetting('default_vat_percentage', settings.default_vat_percentage)} /></div>
                <div><Label>Invoice Prefix</Label><Input value={settings.invoice_prefix || 'INV'} onChange={(e) => setSettings(p => ({ ...p, invoice_prefix: e.target.value }))} onBlur={() => handleSaveSetting('invoice_prefix', settings.invoice_prefix)} /></div>
                <div className="col-span-full"><Label>Invoice Terms & Conditions</Label><Textarea value={settings.invoice_terms || ''} onChange={(e) => setSettings(p => ({ ...p, invoice_terms: e.target.value }))} onBlur={() => handleSaveSetting('invoice_terms', settings.invoice_terms)} rows={3} /></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Emirates / Locations</CardTitle><CardDescription>UAE Emirates for client and worker addresses</CardDescription></div>
              {isAdmin && <Button onClick={() => openDialog('emirate')} data-testid="add-emirate-btn"><Plus className="w-4 h-4 mr-2" /> Add Emirate</Button>}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Arabic</TableHead><TableHead>Order</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {emirates.map((emi) => (
                    <TableRow key={emi.id}>
                      <TableCell className="font-mono">{emi.code}</TableCell>
                      <TableCell className="font-medium">{emi.name}</TableCell>
                      <TableCell dir="rtl">{emi.name_arabic || '-'}</TableCell>
                      <TableCell>{emi.display_order}</TableCell>
                      {isAdmin && <TableCell className="text-right">
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog('emirate', emi)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteItem('emirate', emi.id)} className="text-red-500"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>}
                    </TableRow>
                  ))}
                  {emirates.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No emirates configured</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Service Categories</CardTitle><CardDescription>Organize services into categories</CardDescription></div>
              {isAdmin && <Button onClick={() => openDialog('serviceCategory')} data-testid="add-service-category-btn"><Plus className="w-4 h-4 mr-2" /> Add Category</Button>}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Icon</TableHead><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Description</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {serviceCategories.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell className="text-2xl">{cat.icon}</TableCell>
                      <TableCell className="font-mono">{cat.code}</TableCell>
                      <TableCell className="font-medium">{cat.name}</TableCell>
                      <TableCell className="text-muted-foreground">{cat.description || '-'}</TableCell>
                      {isAdmin && <TableCell className="text-right">
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog('serviceCategory', cat)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteItem('serviceCategory', cat.id)} className="text-red-500"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Document Types</CardTitle><CardDescription>Types of documents that can be tracked</CardDescription></div>
              {isAdmin && <Button onClick={() => openDialog('documentType')} data-testid="add-document-type-btn"><Plus className="w-4 h-4 mr-2" /> Add Type</Button>}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Applicable To</TableHead><TableHead>Has Expiry</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {documentTypes.map((dt) => (
                    <TableRow key={dt.id}>
                      <TableCell className="font-mono">{dt.code}</TableCell>
                      <TableCell className="font-medium">{dt.name}</TableCell>
                      <TableCell><Badge variant="secondary">{dt.applicable_to || 'Both'}</Badge></TableCell>
                      <TableCell><Badge variant={dt.requires_expiry !== false ? 'default' : 'secondary'}>{dt.requires_expiry !== false ? 'Yes' : 'No'}</Badge></TableCell>
                      {isAdmin && <TableCell className="text-right">
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog('documentType', dt)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteItem('documentType', dt.id)} className="text-red-500"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Service Order Statuses</CardTitle><CardDescription>Workflow statuses for service orders</CardDescription></div>
              {isAdmin && <Button onClick={() => openDialog('orderStatus')} data-testid="add-order-status-btn"><Plus className="w-4 h-4 mr-2" /> Add Status</Button>}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Color</TableHead><TableHead>Type</TableHead><TableHead>Order</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {orderStatuses.map((status) => (
                    <TableRow key={status.id}>
                      <TableCell className="font-mono">{status.code}</TableCell>
                      <TableCell className="font-medium">{status.name}</TableCell>
                      <TableCell><div className="flex items-center gap-2"><div className="w-4 h-4 rounded" style={{backgroundColor: status.color}} />{status.color}</div></TableCell>
                      <TableCell>
                        {status.is_initial && <Badge variant="outline" className="mr-1">Initial</Badge>}
                        {status.is_final && <Badge variant="outline">Final</Badge>}
                        {!status.is_initial && !status.is_final && <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>{status.display_order}</TableCell>
                      {isAdmin && <TableCell className="text-right">
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog('orderStatus', status)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteItem('orderStatus', status.id)} className="text-red-500"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>}
                    </TableRow>
                  ))}
                  {orderStatuses.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No order statuses configured</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="mt-6 space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Payment Terms</CardTitle><CardDescription>Invoice payment term options</CardDescription></div>
              {isAdmin && <Button onClick={() => openDialog('paymentTerm')} data-testid="add-payment-term-btn"><Plus className="w-4 h-4 mr-2" /> Add Term</Button>}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Days</TableHead><TableHead>Order</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {paymentTerms.map((term) => (
                    <TableRow key={term.id}>
                      <TableCell className="font-mono">{term.code}</TableCell>
                      <TableCell className="font-medium">{term.name}</TableCell>
                      <TableCell>{term.days} days</TableCell>
                      <TableCell>{term.display_order}</TableCell>
                      {isAdmin && <TableCell className="text-right">
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog('paymentTerm', term)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteItem('paymentTerm', term.id)} className="text-red-500"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>}
                    </TableRow>
                  ))}
                  {paymentTerms.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No payment terms configured</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Payment Methods</CardTitle><CardDescription>Available payment methods for recording payments</CardDescription></div>
              {isAdmin && <Button onClick={() => openDialog('paymentMethod')} data-testid="add-payment-method-btn"><Plus className="w-4 h-4 mr-2" /> Add Method</Button>}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Icon</TableHead><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Arabic</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {paymentMethods.map((method) => (
                    <TableRow key={method.id}>
                      <TableCell className="text-2xl">{method.icon}</TableCell>
                      <TableCell className="font-mono">{method.code}</TableCell>
                      <TableCell className="font-medium">{method.name}</TableCell>
                      <TableCell dir="rtl">{method.name_arabic || '-'}</TableCell>
                      {isAdmin && <TableCell className="text-right">
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog('paymentMethod', method)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteItem('paymentMethod', method.id)} className="text-red-500"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>}
                    </TableRow>
                  ))}
                  {paymentMethods.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No payment methods configured</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Expense Categories</CardTitle><CardDescription>Categories for tracking business expenses</CardDescription></div>
              {isAdmin && <Button onClick={() => openDialog('expenseCategory')} data-testid="add-expense-category-btn"><Plus className="w-4 h-4 mr-2" /> Add Category</Button>}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Arabic</TableHead><TableHead>Description</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {expenseCategories.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell className="font-medium">{cat.name}</TableCell>
                      <TableCell dir="rtl">{cat.name_arabic || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{cat.description || '-'}</TableCell>
                      {isAdmin && <TableCell className="text-right">
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog('expenseCategory', cat)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteItem('expenseCategory', cat.id)} className="text-red-500"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Fields Tab */}
        <TabsContent value="fields" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Custom Fields</CardTitle><CardDescription>Add custom fields to entities without code changes</CardDescription></div>
              {isAdmin && <Button onClick={() => openDialog('customField')} data-testid="add-custom-field-btn"><Plus className="w-4 h-4 mr-2" /> Add Field</Button>}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Entity</TableHead><TableHead>Field Name</TableHead><TableHead>Label</TableHead><TableHead>Type</TableHead><TableHead>Required</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {customFields.map((field) => (
                    <TableRow key={field.id}>
                      <TableCell><Badge variant="outline">{field.entity_type}</Badge></TableCell>
                      <TableCell className="font-mono text-sm">{field.field_name}</TableCell>
                      <TableCell className="font-medium">{field.field_label}</TableCell>
                      <TableCell><Badge variant="secondary">{field.field_type}</Badge></TableCell>
                      <TableCell><Badge variant={field.is_required ? 'destructive' : 'secondary'}>{field.is_required ? 'Yes' : 'No'}</Badge></TableCell>
                      {isAdmin && <TableCell className="text-right">
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog('customField', field)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteItem('customField', field.id)} className="text-red-500"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>}
                    </TableRow>
                  ))}
                  {customFields.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No custom fields defined yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        {isAdmin && <TabsContent value="users" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>User Management</CardTitle><CardDescription>Manage system users and their roles</CardDescription></div>
              <Button onClick={() => openDialog('user')} data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" /> Add User</Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.email}</TableCell>
                      <TableCell>{u.full_name}</TableCell>
                      <TableCell><Badge variant={u.role === 'Admin' ? 'default' : 'secondary'}><Shield className="w-3 h-3 mr-1" /> {u.role}</Badge></TableCell>
                      <TableCell><Badge variant={u.is_active ? 'default' : 'destructive'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog('user', u)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            {u.id !== user?.id && <DropdownMenuItem onClick={() => handleDeleteItem('user', u.id)} className="text-red-500"><Trash2 className="w-4 h-4 mr-2" /> Deactivate</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>}

        {/* Permissions Tab */}
        {isAdmin && <TabsContent value="permissions" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Role-Based Permissions</CardTitle>
                  <CardDescription>Configure what each role can access and modify</CardDescription>
                </div>
                <Button variant="outline" onClick={handleResetPermissions}>
                  Reset to Defaults
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={permissionRoles[0] || 'Admin'} className="w-full">
                <TabsList className="mb-4">
                  {permissionRoles.map(role => (
                    <TabsTrigger key={role} value={role}>{role}</TabsTrigger>
                  ))}
                </TabsList>
                {permissionRoles.map(role => (
                  <TabsContent key={role} value={role}>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-[200px]">Module</TableHead>
                            {permissionModules[0]?.actions?.map(action => (
                              <TableHead key={action} className="text-center capitalize w-[100px]">
                                {action.replace(/_/g, ' ')}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {permissionModules.map(module => (
                            <TableRow key={module.key}>
                              <TableCell className="font-medium">{module.name}</TableCell>
                              {module.actions.map(action => (
                                <TableCell key={action} className="text-center">
                                  <Switch
                                    checked={permissions[role]?.[module.key]?.[action] || false}
                                    onCheckedChange={(checked) => handlePermissionChange(role, module.key, action, checked)}
                                    disabled={role === 'Admin' && module.key === 'permissions'}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="flex justify-end mt-4">
                      <Button 
                        onClick={() => handleSavePermissions(role)} 
                        disabled={savingPermissions}
                        className="gap-2"
                      >
                        <Save className="w-4 h-4" />
                        {savingPermissions ? 'Saving...' : `Save ${role} Permissions`}
                      </Button>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>}

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6">
          <Card>
            <CardHeader><CardTitle>Alert Settings</CardTitle><CardDescription>Configure document expiry and payment reminders</CardDescription></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Expiry Alert Days (Critical)</Label><Input type="number" value={settings.expiry_alert_critical || '7'} onChange={(e) => setSettings(p => ({ ...p, expiry_alert_critical: e.target.value }))} onBlur={() => handleSaveSetting('expiry_alert_critical', settings.expiry_alert_critical)} /><p className="text-xs text-muted-foreground mt-1">Documents expiring within these days show as critical</p></div>
                <div><Label>Expiry Alert Days (Warning)</Label><Input type="number" value={settings.expiry_alert_warning || '30'} onChange={(e) => setSettings(p => ({ ...p, expiry_alert_warning: e.target.value }))} onBlur={() => handleSaveSetting('expiry_alert_warning', settings.expiry_alert_warning)} /></div>
                <div><Label>Payment Overdue Days (Reminder)</Label><Input type="number" value={settings.payment_reminder_days || '7'} onChange={(e) => setSettings(p => ({ ...p, payment_reminder_days: e.target.value }))} onBlur={() => handleSaveSetting('payment_reminder_days', settings.payment_reminder_days)} /></div>
                <div><Label>Auto-Generate Reminders</Label><div className="flex items-center gap-2 mt-2"><Switch checked={settings.reminder_enabled === 'true'} onCheckedChange={(v) => { setSettings(p => ({ ...p, reminder_enabled: v ? 'true' : 'false' })); handleSaveSetting('reminder_enabled', v ? 'true' : 'false'); }} /><span className="text-sm text-muted-foreground">{settings.reminder_enabled === 'true' ? 'Enabled' : 'Disabled'}</span></div></div>
              </div>
              <div className="pt-4 border-t"><p className="text-sm text-muted-foreground"><strong>Note:</strong> Email/SMS/WhatsApp notifications are currently **mocked**. Contact support to enable real notification delivery.</p></div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog for all item types */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{getDialogTitle()}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Service Category Form */}
            {editingItem?.type === 'serviceCategory' && (<>
              <div className="grid grid-cols-2 gap-4"><div><Label>Code *</Label><Input value={formData.code || ''} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="VISA" /></div><div><Label>Icon</Label><Input value={formData.icon || ''} onChange={(e) => setFormData(p => ({ ...p, icon: e.target.value }))} placeholder="📋" /></div></div>
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Visa Services" /></div>
              <div><Label>Description</Label><Input value={formData.description || ''} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} /></div>
            </>)}

            {/* Document Type Form */}
            {editingItem?.type === 'documentType' && (<>
              <div><Label>Code *</Label><Input value={formData.code || ''} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="PASSPORT" /></div>
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Passport" /></div>
              <div><Label>Applicable To</Label><Select value={formData.applicable_to} onValueChange={(v) => setFormData(p => ({ ...p, applicable_to: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Client">Client Only</SelectItem><SelectItem value="Worker">Worker Only</SelectItem><SelectItem value="Both">Both</SelectItem></SelectContent></Select></div>
              <div className="flex items-center gap-2"><Switch checked={formData.requires_expiry} onCheckedChange={(v) => setFormData(p => ({ ...p, requires_expiry: v }))} /><Label>Has Expiry Date</Label></div>
            </>)}

            {/* Expense Category Form */}
            {editingItem?.type === 'expenseCategory' && (<>
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Office Supplies" /></div>
              <div><Label>Name (Arabic)</Label><Input value={formData.name_arabic || ''} onChange={(e) => setFormData(p => ({ ...p, name_arabic: e.target.value }))} dir="rtl" /></div>
              <div><Label>Description</Label><Input value={formData.description || ''} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} /></div>
            </>)}

            {/* Custom Field Form */}
            {editingItem?.type === 'customField' && (<>
              <div><Label>Entity Type *</Label><Select value={formData.entity_type} onValueChange={(v) => setFormData(p => ({ ...p, entity_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{entityTypes.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-4"><div><Label>Field Name *</Label><Input value={formData.field_name || ''} onChange={(e) => setFormData(p => ({ ...p, field_name: e.target.value.toLowerCase().replace(/\s/g, '_') }))} placeholder="custom_field" /></div><div><Label>Label *</Label><Input value={formData.field_label || ''} onChange={(e) => setFormData(p => ({ ...p, field_label: e.target.value }))} placeholder="Custom Field" /></div></div>
              <div><Label>Field Type *</Label><Select value={formData.field_type} onValueChange={(v) => setFormData(p => ({ ...p, field_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{fieldTypes.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent></Select></div>
              {(formData.field_type === 'Select' || formData.field_type === 'Multi Select') && <div><Label>Options (comma-separated)</Label><Input value={formData.field_options || ''} onChange={(e) => setFormData(p => ({ ...p, field_options: e.target.value }))} placeholder="Option 1, Option 2, Option 3" /></div>}
              <div><Label>Help Text</Label><Input value={formData.help_text || ''} onChange={(e) => setFormData(p => ({ ...p, help_text: e.target.value }))} /></div>
              <div className="flex items-center gap-2"><Switch checked={formData.is_required} onCheckedChange={(v) => setFormData(p => ({ ...p, is_required: v }))} /><Label>Required Field</Label></div>
            </>)}

            {/* User Form */}
            {editingItem?.type === 'user' && (<>
              <div><Label>Email *</Label><Input type="email" value={formData.email || ''} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} disabled={!!editingItem?.id} /></div>
              <div><Label>Full Name *</Label><Input value={formData.full_name || ''} onChange={(e) => setFormData(p => ({ ...p, full_name: e.target.value }))} /></div>
              <div><Label>Role *</Label><Select value={formData.role} onValueChange={(v) => setFormData(p => ({ ...p, role: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{userRoles.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent></Select></div>
              <div><Label>{editingItem?.id ? 'New Password (leave blank to keep current)' : 'Password *'}</Label><Input type="password" value={formData.password || ''} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))} /></div>
              {editingItem?.id && <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(v) => setFormData(p => ({ ...p, is_active: v }))} /><Label>Active</Label></div>}
            </>)}

            {/* Emirate Form */}
            {editingItem?.type === 'emirate' && (<>
              <div><Label>Code *</Label><Input value={formData.code || ''} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="DXB" /></div>
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Dubai" /></div>
              <div><Label>Name (Arabic)</Label><Input value={formData.name_arabic || ''} onChange={(e) => setFormData(p => ({ ...p, name_arabic: e.target.value }))} dir="rtl" placeholder="دبي" /></div>
              <div><Label>Display Order</Label><Input type="number" value={formData.display_order || 0} onChange={(e) => setFormData(p => ({ ...p, display_order: parseInt(e.target.value) || 0 }))} /></div>
            </>)}

            {/* Payment Term Form */}
            {editingItem?.type === 'paymentTerm' && (<>
              <div><Label>Code *</Label><Input value={formData.code || ''} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="NET30" /></div>
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Net 30 Days" /></div>
              <div><Label>Days</Label><Input type="number" value={formData.days || 0} onChange={(e) => setFormData(p => ({ ...p, days: parseInt(e.target.value) || 0 }))} /></div>
              <div><Label>Display Order</Label><Input type="number" value={formData.display_order || 0} onChange={(e) => setFormData(p => ({ ...p, display_order: parseInt(e.target.value) || 0 }))} /></div>
            </>)}

            {/* Payment Method Form */}
            {editingItem?.type === 'paymentMethod' && (<>
              <div className="grid grid-cols-2 gap-4"><div><Label>Code *</Label><Input value={formData.code || ''} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="CASH" /></div><div><Label>Icon</Label><Input value={formData.icon || ''} onChange={(e) => setFormData(p => ({ ...p, icon: e.target.value }))} placeholder="💵" /></div></div>
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Cash" /></div>
              <div><Label>Name (Arabic)</Label><Input value={formData.name_arabic || ''} onChange={(e) => setFormData(p => ({ ...p, name_arabic: e.target.value }))} dir="rtl" /></div>
              <div><Label>Display Order</Label><Input type="number" value={formData.display_order || 0} onChange={(e) => setFormData(p => ({ ...p, display_order: parseInt(e.target.value) || 0 }))} /></div>
            </>)}

            {/* Order Status Form */}
            {editingItem?.type === 'orderStatus' && (<>
              <div className="grid grid-cols-2 gap-4"><div><Label>Code *</Label><Input value={formData.code || ''} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="NEW" /></div><div><Label>Color</Label><Input type="color" value={formData.color || '#6B7280'} onChange={(e) => setFormData(p => ({ ...p, color: e.target.value }))} className="h-10" /></div></div>
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="New" /></div>
              <div><Label>Name (Arabic)</Label><Input value={formData.name_arabic || ''} onChange={(e) => setFormData(p => ({ ...p, name_arabic: e.target.value }))} dir="rtl" /></div>
              <div><Label>Display Order</Label><Input type="number" value={formData.display_order || 0} onChange={(e) => setFormData(p => ({ ...p, display_order: parseInt(e.target.value) || 0 }))} /></div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2"><Switch checked={formData.is_initial} onCheckedChange={(v) => setFormData(p => ({ ...p, is_initial: v }))} /><Label>Initial Status</Label></div>
                <div className="flex items-center gap-2"><Switch checked={formData.is_final} onCheckedChange={(v) => setFormData(p => ({ ...p, is_final: v }))} /><Label>Final Status</Label></div>
              </div>
            </>)}

            {/* Save Buttons */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveItem} disabled={saving} data-testid="save-item-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
